import yaml from "js-yaml";
import { z } from "zod";

// Mini-Frontmatter-Parser, browser-safe (gray-matter zieht Node-Buffer rein
// und kracht im Browser mit "Buffer is not defined"). Wir brauchen nur YAML
// im klassischen ---/--- Block — alles weitere unterstützt gray-matter zwar,
// aber wir nutzen es nicht. js-yaml ist explizite Dep.
const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function parseFrontmatter(raw: string): { data: unknown; content: string } {
  const normalized = raw.replace(/^﻿/, "");
  const match = normalized.match(FRONTMATTER_RE);
  if (!match) return { data: {}, content: normalized };
  const yamlBlock = match[1] ?? "";
  const content = normalized.slice(match[0].length);
  const data = yaml.load(yamlBlock) ?? {};
  return { data, content };
}

// Frontmatter-Schema für jede Session-Datei in apps/web/src/content/timeline/NN-name.md:
//
//   ---
//   session: 1                          # Pflicht, int 1-8
//   title: "Grundverständnis"           # Pflicht
//   subtitle: "..."                     # Pflicht
//   duration: "120 min"                  # Pflicht
//   date: "2026-05-12"                   # Optional, ISO YYYY-MM-DD
//   focus: "..."                        # Pflicht, eine Zeile
//   homework: "..."                      # Optional, eine Zeile
//   status: "upcoming"                   # Optional, default "upcoming"
//   ---
//
// _meta.md hat ein eigenes, kleineres Schema (siehe TimelineMetaSchema).

const SessionFrontmatterSchema = z.object({
  session: z.number().int().min(1).max(8),
  title: z.string().min(1),
  subtitle: z.string().min(1),
  duration: z.string().min(1),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "date muss ISO YYYY-MM-DD sein")
    .optional(),
  focus: z.string().min(1),
  homework: z.string().optional(),
  status: z.enum(["upcoming", "current", "completed"]).default("upcoming"),
});

const MetaFrontmatterSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().min(1),
});

export type TimelineSessionMeta = z.infer<typeof SessionFrontmatterSchema>;

export type TimelineSession = {
  meta: TimelineSessionMeta;
  body: string;
  slug: string;
};

export type TimelineMeta = {
  meta: z.infer<typeof MetaFrontmatterSchema>;
  body: string;
};

const rawModules = import.meta.glob("../content/timeline/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

function fileSlug(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.md$/, "");
}

function parseSession(path: string, raw: string): TimelineSession {
  const parsed = parseFrontmatter(raw);
  const result = SessionFrontmatterSchema.safeParse(parsed.data);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join(".")}: ${i.message}`)
      .join("\n");
    throw new Error(
      `[timeline-loader] Ungültiges Frontmatter in ${path}:\n${issues}`,
    );
  }
  return {
    meta: result.data,
    body: parsed.content.trim(),
    slug: fileSlug(path),
  };
}

function parseMeta(path: string, raw: string): TimelineMeta {
  const parsed = parseFrontmatter(raw);
  const result = MetaFrontmatterSchema.safeParse(parsed.data);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join(".")}: ${i.message}`)
      .join("\n");
    throw new Error(
      `[timeline-loader] Ungültiges Frontmatter in ${path}:\n${issues}`,
    );
  }
  return { meta: result.data, body: parsed.content.trim() };
}

function buildSessions(): TimelineSession[] {
  const sessions: TimelineSession[] = [];
  for (const path of Object.keys(rawModules).sort()) {
    const file = path.split("/").pop() ?? "";
    if (file.startsWith("_")) continue;
    const raw = rawModules[path];
    if (raw === undefined) continue;
    sessions.push(parseSession(path, raw));
  }
  return sessions;
}

function buildMeta(): TimelineMeta {
  const entry = Object.entries(rawModules).find(([p]) =>
    (p.split("/").pop() ?? "").startsWith("_meta"),
  );
  if (!entry) {
    throw new Error("[timeline-loader] _meta.md fehlt in content/timeline/");
  }
  const [path, raw] = entry;
  return parseMeta(path, raw);
}

export const TIMELINE_SESSIONS: TimelineSession[] = buildSessions();
export const TIMELINE_META: TimelineMeta = buildMeta();

// Wird beim Build durch Vite ersetzt (define-Plugin in vite.config) — fallback
// auf aktuelles Datum zur Laufzeit, falls die Replacement-Konstante fehlt.
declare const __BUILD_DATE__: string | undefined;
export const TIMELINE_BUILD_DATE: string =
  typeof __BUILD_DATE__ === "string" && __BUILD_DATE__.length > 0
    ? __BUILD_DATE__
    : new Date().toISOString().slice(0, 10);
